import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import NavItem from '@/components/header/NavItem';
import { Button } from '@/components/ui/button';
import { Menu } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useAuth } from '@/contexts/AuthContext';
import UserMenu from '@/components/header/UserMenu';
import { Link } from 'react-router-dom';

const MobileSheet = ({ appName, logoUrl, filteredNavLinks, user, t, isLanguageReady, setIsSheetOpen, isSheetOpen }) => {
  const { logoutUser, loading: authLoading } = useAuth();

  const companyLinks = [
    { name: t('footer.about'), path: '/about' },
    { name: t('footer.features'), path: '/features' },
    { name: t('footer.whyUrbanMint'), path: '/why-urbanmint' },
    { name: t('footer.sponsors'), path: '/sponsors' },
    { name: t('footer.contact'), path: '/contact' },
  ];

  const resourcesLinks = [
    { name: t('nav.learnHub'), path: '/learn' },
    { name: t('nav.freeResources'), path: '/free-resources' },
    { name: t('nav.glossary'), path: '/glossary' },
    { name: t('nav.scenarios'), path: '/scenarios' },
  ];

  const supportLinks = [
    { name: t('footer.helpRequest'), path: '/help-request' },
    { name: t('footer.support'), path: '/support' },
  ];

  const legalLinks = [
    { name: t('footer.privacy'), path: '/privacy' },
    { name: t('footer.terms'), path: '/terms' },
  ];

  const loginLinks = [
    { name: t('nav.login'), path: '/login' },
    { name: t('nav.register'), path: '/register' },
  ];

  const sheetLinks = useMemo(
    () => [
      ...filteredNavLinks,
      ...(!user ? loginLinks : []),
      ...companyLinks,
      ...resourcesLinks,
      ...supportLinks,
      ...legalLinks,
    ],
    [filteredNavLinks, user, t, isLanguageReady]
  );

  const handleLogout = async () => {
    await logoutUser();
    setIsSheetOpen(false);
  };
  
  const renderNavLink = (link, index) => (
    <SheetClose asChild key={link.name + index}>
      <NavItem
        path={link.path}
        icon={link.icon}
        name={link.name}
        isSheetLink={true}
        onClick={() => setIsSheetOpen(false)}
      />
    </SheetClose>
  );

  return (
    <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden" aria-label={isLanguageReady ? t('header.openMenu') : ''}>
          <Menu className="w-6 h-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="flex flex-col p-0 w-full sm:max-w-xs">
        <SheetHeader className="p-4 border-b">
          <div className="flex justify-between items-center">
            <SheetTitle className="flex gap-2 items-center text-lg font-semibold">
              <img 
                src="/assets/logo_Logo.png" 
                alt={isLanguageReady ? t('header.logoAlt', { appName }) : `${appName} logo`} 
                className="object-contain h-10" 
              />
            </SheetTitle>
          </div>
        </SheetHeader>
                
        <ScrollArea className="flex-grow p-4">
          <nav className="grid gap-2">
            {sheetLinks.map(renderNavLink)}
          </nav>
        </ScrollArea>

        <div className="p-4 border-t">
          {user ? (
            <div className="space-y-3">
              <UserMenu isSheetContext={true} onSheetItemClick={() => setIsSheetOpen(false)}/>
              <Button variant="outline" className="w-full" onClick={handleLogout} disabled={authLoading}>
                {isLanguageReady && t ? (authLoading ? t('userMenu.loggingOut') : t('userMenu.logout')) : 'Log Out'}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <SheetClose asChild>
                <Button variant="outline" asChild>
                  <Link to="/login">{isLanguageReady && t ? t('nav.login') : 'Login'}</Link>
                </Button>
              </SheetClose>
              <SheetClose asChild>
                <Button asChild>
                  <Link to="/register">{isLanguageReady && t ? t('nav.register') : 'Register'}</Link>
                </Button>
              </SheetClose>
            </div>
          )}
          <div className="flex justify-between items-center mt-4">
            <LanguageSwitcher isSheetContext={true} />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileSheet;

MobileSheet.propTypes = {
  appName: PropTypes.string.isRequired,
  logoUrl: PropTypes.string,
  filteredNavLinks: PropTypes.array.isRequired,
  user: PropTypes.object,
  t: PropTypes.func.isRequired,
  isLanguageReady: PropTypes.bool.isRequired,
  setIsSheetOpen: PropTypes.func.isRequired,
  isSheetOpen: PropTypes.bool.isRequired,
};
  